<template>
  <div class="container">
    <main class="main-layout">
      <section class="card upload-section left-card">
        <div class="card-header" style="display: flex; justify-content: center; align-items: center; height: 80px;">
          <h2>Image Upload</h2>
        </div>
        <div class="card-body">
          <form @submit.prevent="handleSubmit" class="upload-form">
            <label for="imageInput" class="file-label">
              <span class="file-icon">
                <i class="fas fa-upload"></i>
              </span>
              <span class="file-text" v-if="!imageFile">Click or drag image here to upload</span>
              <span class="file-text" v-if="imageFile">{{ imageFile.name }}</span>
              <input 
                type="file" 
                id="imageInput" 
                name="image" 
                accept="image/*" 
                required
                @change="handleImageChange"
                class="file-input"
              >
            </label>
            <button type="submit" class="btn btn-primary upload-btn" :disabled="!imageFile || isLoading">
              <span v-if="!isLoading">Upload and Segment</span>
              <span v-if="isLoading">
                <i class="fas fa-spinner fa-spin"></i> Processing...
              </span>
            </button>
          </form>
        </div>
      </section>

      <section class="card image-compare-section right-card">
        <div class="card-header" style="display: flex; justify-content: center; align-items: center; height: 80px;">
          <h2>Original & Segmentation</h2>
        </div>
        <div class="card-body">
          <div class="image-compare-container">
            <div class="image-item">
              <h3 class="image-title">Original</h3>
              <div class="image-box">
                <img v-if="imagePreviewUrl" :src="imagePreviewUrl" class="display-image" alt="Original">
                <div v-else class="image-placeholder">No image uploaded</div>
              </div>
            </div>

            <div class="image-item">
              <h3 class="image-title">Segmentation</h3>
              <div class="image-box">
                <img v-if="overlayImageUrl" :src="overlayImageUrl" class="display-image" alt="Segmentation">
                <div v-else class="image-placeholder">Waiting for segmentation</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    
    <footer class="page-footer">
      <p> Pig Segmentation System &copy; 2025</p>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const imageFile = ref(null);
const imagePreviewUrl = ref('');
const overlayImageUrl = ref(''); // 分割叠加图
const isLoading = ref(false);
const errorMessage = ref('');

const handleImageChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    imageFile.value = file;
    const reader = new FileReader();
    reader.onload = (e) => imagePreviewUrl.value = e.target.result;
    reader.readAsDataURL(file);
  }
};

const resetResults = () => {
  overlayImageUrl.value = '';
  errorMessage.value = '';
};

const fetchWithTimeout = (url, options, timeout = 60000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), timeout))
  ]);
};

// 👇 关键：从 /detect 改成 /segment
const handleSubmit = async () => {
  if (!imageFile.value) return;
  isLoading.value = true;
  resetResults();
  
  try {
    const formData = new FormData();
    formData.append('image', imageFile.value);
    formData.append('tta', 'false');

    const res = await fetchWithTimeout('https://fengguoheng.online/segment', {
      method: 'POST', body: formData
    });
    const data = await res.json();
    
    if (data.overlay_image) {
      overlayImageUrl.value = `data:image/jpg;base64,${data.overlay_image}`;
    }
  } catch (err) {
    errorMessage.value = 'Segmentation failed';
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.container {
  font-family: 'Segoe UI', Roboto, sans-serif;
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
  background: #f8fafc;
  min-height: 100vh;
}

.main-layout {
  display: flex;
  gap: 25px;
  align-items: stretch;
}

.left-card {
  width: 380px;
  flex-shrink: 0;
}

.right-card {
  flex: 1;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  overflow: hidden;
}

.card-header {
  background: #f1f5f9;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  text-align: center;
}

.card-header h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1e293b;
}

.card-body {
  padding: 24px;
}

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.file-label {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 40px 15px;
  text-align: center;
  cursor: pointer;
  background: #f8fafc;
  color: #64748b;
}

.file-label:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.file-icon {
  font-size: 2.2rem;
  margin-bottom: 10px;
}

.file-input {
  display: none;
}

.upload-btn {
  padding: 12px;
  width: 100%;
  font-size: 1rem;
}

.btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.image-compare-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 25px;
  height: 100%;
}

.image-item {
  flex: 1;
  text-align: center;
}

.image-title {
  font-size: 1.2rem;
  margin-bottom: 12px;
  color: #334155;
  font-weight: 600;
}

.image-box {
  width: 100%;
  height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
}

.display-image {
  max-width: 100%;
  max-height: 450px;
  object-fit: contain;
  border-radius: 6px;
}

.page-footer {
  margin-top: 30px;
  text-align: center;
  color: #94a3b8;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 1024px) {
  .main-layout {
    flex-direction: column;
  }
  .left-card {
    width: 100%;
  }
}
</style>